import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { toast } from '@/ui/toast';
import { SocketIoEvents } from '@/events/socket-io.events';
import { ReviewsExportProgress } from '@/context/reviews-page.context';
import type { CsvExportProgressStage } from '@/entities/csv-export-progress-event.entity';

type UseExportProgressOrchestrationParams = {
    events: SocketIoEvents;
    isSocketConnected: boolean;
    setExportProgress: Dispatch<SetStateAction<ReviewsExportProgress | null>>;
};

type UseExportProgressOrchestrationReturn = {
    scheduleExportProgressCleanup: (exportOperationId: string) => void;
};

const isTerminalStage = (stage: CsvExportProgressStage) =>
    stage === 'completed' || stage === 'failed' || stage === 'canceled';

const resolveMonotonicTotalRows = (currentTotalRows: number | null, nextTotalRows: number | null): number | null => {
    if (currentTotalRows === null) {
        return nextTotalRows;
    }

    if (nextTotalRows === null) {
        return currentTotalRows;
    }

    return Math.max(currentTotalRows, nextTotalRows);
};

export const useExportProgressOrchestration = ({
    events,
    isSocketConnected,
    setExportProgress,
}: UseExportProgressOrchestrationParams): UseExportProgressOrchestrationReturn => {
    const exportProgressCleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (exportProgressCleanupTimerRef.current) {
                clearTimeout(exportProgressCleanupTimerRef.current);
                exportProgressCleanupTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const unsubscribe = events.on('csvExportProgress', (data) => {
            setExportProgress((current) => {
                if (!current || current.exportOperationId !== data.exportOperationId) {
                    return current;
                }

                if (data.stage === 'failed') {
                    toast.error(data.errorMessage || 'Ошибка при экспорте CSV');
                }

                const incomingIsTerminal = isTerminalStage(data.stage);
                const currentIsTerminal = !current.isRunning;

                if (currentIsTerminal && !incomingIsTerminal) {
                    return current;
                }

                const processedRows = Math.max(current.processedRows, data.processedRows);
                const totalRows = resolveMonotonicTotalRows(current.totalRows, data.totalRows);

                return {
                    ...current,
                    isRunning: !incomingIsTerminal,
                    processedRows,
                    totalRows,
                    etaSeconds: incomingIsTerminal ? 0 : data.etaSeconds,
                    isSocketConnected: true,
                    isProgressAvailable: true,
                    limitWarning: data.limitWarning ?? current.limitWarning,
                };
            });
        });

        return () => unsubscribe();
    }, [events, setExportProgress]);

    useEffect(() => {
        setExportProgress((current) => {
            if (!current || !current.isRunning) {
                return current;
            }

            if (isSocketConnected) {
                if (current.isSocketConnected && current.isProgressAvailable) {
                    return current;
                }

                return {
                    ...current,
                    isSocketConnected: true,
                    isProgressAvailable: true,
                };
            }

            if (!current.isSocketConnected && !current.isProgressAvailable && current.etaSeconds === null) {
                return current;
            }

            return {
                ...current,
                isSocketConnected: false,
                isProgressAvailable: false,
                etaSeconds: null,
            };
        });
    }, [isSocketConnected, setExportProgress]);

    const scheduleExportProgressCleanup = (exportOperationId: string) => {
        if (exportProgressCleanupTimerRef.current) {
            clearTimeout(exportProgressCleanupTimerRef.current);
        }

        exportProgressCleanupTimerRef.current = setTimeout(() => {
            setExportProgress((current) => {
                if (!current || current.exportOperationId !== exportOperationId) {
                    return current;
                }

                return null;
            });
            exportProgressCleanupTimerRef.current = null;
        }, 120);
    };

    return {
        scheduleExportProgressCleanup,
    };
};
